import {
  Box,
  Button,
  Text,
  VStack,
  Spinner,
  Avatar,
  FormControl,
  FormLabel,
  Input,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from "@chakra-ui/react";
import TrackingPageLayout from "components/TrackingPageLayout";
import { useTrackingProfile } from "hooks/useTrackingProfile";
import { useState, useCallback } from "react";
import { showToast, toastTypes } from "utils";

export default function TrackingProfilePage() {
  const toast = useToast();
  const { profile, isLoading, error, changePassword } = useTrackingProfile();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changing, setChanging] = useState(false);

  const handleChangePassword = useCallback(async () => {
    if (!newPassword || newPassword !== confirmPassword) {
      showToast({ toast, message: "Les mots de passe ne correspondent pas.", status: toastTypes.error });
      return;
    }
    if (newPassword.length < 8) {
      showToast({ toast, message: "Le nouveau mot de passe doit faire au moins 8 caractères.", status: toastTypes.error });
      return;
    }
    setChanging(true);
    try {
      await changePassword({
        old_password: oldPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      showToast({ toast, message: "Mot de passe modifié avec succès.", status: toastTypes.success });
      onClose();
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || "Erreur lors du changement de mot de passe.";
      showToast({ toast, message: msg, status: toastTypes.error });
    } finally {
      setChanging(false);
    }
  }, [changePassword, oldPassword, newPassword, confirmPassword, toast, onClose]);

  return (
    <TrackingPageLayout title="Mon profil (Tracking)">
      {error && (
        <Text color="red.600" mb={4}>
          Erreur lors du chargement du profil.
        </Text>
      )}

      {isLoading && !profile ? (
        <Box py={8} display="flex" justifyContent="center">
          <Spinner size="lg" />
        </Box>
      ) : profile ? (
        <VStack align="stretch" spacing={4} bg="white" p={6} borderRadius="md" shadow="sm">
          <Box display="flex" alignItems="center" gap={4}>
            <Avatar
              size="xl"
              name={profile.first_name && profile.last_name ? `${profile.first_name} ${profile.last_name}` : profile.username}
              src={profile.profile_image || profile.profile_picture || undefined}
            />
            <Box>
              <Text fontWeight="bold" fontSize="lg">
                {profile.first_name || profile.last_name
                  ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim()
                  : profile.username}
              </Text>
              <Text color="gray.600">{profile.email || "—"}</Text>
              <Text fontSize="sm" color="gray.500">
                @{profile.username}
              </Text>
              {profile.phone_number && (
                <Text fontSize="sm" mt={1}>
                  Tél. {profile.phone_number}
                </Text>
              )}
            </Box>
          </Box>
          <Button size="sm" variant="outline" alignSelf="flex-start" onClick={onOpen}>
            Changer le mot de passe
          </Button>
        </VStack>
      ) : (
        <Text color="gray.600">Aucune donnée de profil disponible.</Text>
      )}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Changer le mot de passe</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Mot de passe actuel</FormLabel>
                <Input
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder="Mot de passe actuel"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Nouveau mot de passe</FormLabel>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Nouveau mot de passe (min. 8 caractères)"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Confirmer le nouveau mot de passe</FormLabel>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirmer"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Annuler
            </Button>
            <Button colorScheme="blue" onClick={handleChangePassword} isLoading={changing}>
              Enregistrer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </TrackingPageLayout>
  );
}
